import React, { useState, useEffect } from 'react';
import { api } from '../api';
import ActionMenu from '../components/ActionMenu';
import { Script, Parameter, Feed } from '../types';
import { useNotification } from '../components/NotificationContext';

const ManageIngestion: React.FC = () => {
    const { notify } = useNotification();
    const [feeds, setFeeds] = useState<Feed[]>([]);
    const [scripts, setScripts] = useState<Script[]>([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalFeeds, setTotalFeeds] = useState(0);

    const [isFeedModalOpen, setIsFeedModalOpen] = useState(false);
    const [isScriptModalOpen, setIsScriptModalOpen] = useState(false);
    const [isLogModalOpen, setIsLogModalOpen] = useState(false);

    const [selectedLog, setSelectedLog] = useState('');
    const [message, setMessage] = useState('');

    // Feed Form State
    const [feedFormData, setFeedFormData] = useState({
        id: null as number | null,
        name: '',
        script_id: '',
        parameters: [] as { label: string, value: string }[]
    });

    // Script Form State
    const [scriptFormData, setScriptFormData] = useState({
        id: null as number | null,
        name: '',
        description: '',
        path: '',
        parameters: [] as Parameter[]
    });

    useEffect(() => {
        fetchFeeds(currentPage);
        fetchScripts();
    }, [currentPage]);

    // Periodic polling for real-time progress
    useEffect(() => {
        const interval = setInterval(() => {
            fetchFeeds(currentPage);
        }, 5000);
        return () => clearInterval(interval);
    }, [currentPage]);

    const fetchFeeds = async (page: number) => {
        try {
            const data = await api.get(`/admin/feeds?page=${page}`);
            setFeeds(data.feeds);
            setTotalPages(data.pages);
            setTotalFeeds(data.total);
            setCurrentPage(data.current_page);
        } catch (error: any) {
            console.error('Failed to fetch feeds:', error);
        }
    };

    const fetchScripts = async () => {
        try {
            const data = await api.get('/admin/scripts');
            setScripts(data.scripts);
        } catch (error: any) {
            setMessage('Failed to fetch scripts: ' + error.message);
        }
    };

    const handleFeedSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const payload = {
                name: feedFormData.name,
                script_id: parseInt(feedFormData.script_id),
                parameters: feedFormData.parameters
            };
            if (feedFormData.id) {
                await api.put(`/admin/feeds/${feedFormData.id}`, payload);
                notify('Feed aggiornato con successo!', 'success');
            } else {
                await api.post('/admin/feeds', payload);
                notify('Feed creato con successo!', 'success');
            }
            setIsFeedModalOpen(false);
            setFeedFormData({ id: null, name: '', script_id: '', parameters: [] });
            fetchFeeds(currentPage);
        } catch (error: any) {
            notify('Errore salvataggio feed: ' + error.message, 'error');
        }
    };

    const handleScriptSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (scriptFormData.id) {
                await api.put(`/admin/scripts/${scriptFormData.id}`, scriptFormData);
                notify('Script aggiornato con successo!', 'success');
            } else {
                await api.post('/admin/scripts', scriptFormData);
                notify('Script creato con successo!', 'success');
            }
            setIsScriptModalOpen(false);
            setScriptFormData({ id: null, name: '', description: '', path: '', parameters: [] });
            fetchScripts();
        } catch (error: any) {
            notify('Errore salvataggio script: ' + error.message, 'error');
        }
    };

    const startFeed = async (id: number) => {
        try {
            await api.post(`/admin/feeds/${id}/start`, {});
            fetchFeeds(currentPage);
        } catch (error: any) {
            notify('Errore avvio feed.', 'error');
        }
    };

    const fetchFeedLog = async (id: number) => {
        try {
            const data = await api.get(`/admin/feeds/${id}/log`);
            setSelectedLog(data.log);
            setIsLogModalOpen(true);
        } catch (error: any) {
            notify('Errore caricamento log.', 'error');
        }
    };

    const resetFeed = async (id: number) => {
        if (window.confirm('Questa operazione forzerà lo stato a "Stopped". Usalo solo se il feed è bloccato. Continuare?')) {
            try {
                await api.post(`/admin/feeds/${id}/reset`, {});
                notify('Stato feed resettato.', 'success');
                fetchFeeds(currentPage);
            } catch (error: any) {
                notify('Errore reset feed.', 'error');
            }
        }
    };
    const stopFeed = async (id: number) => {
        try {
            await api.post(`/admin/feeds/${id}/stop`, {});
            fetchFeeds(currentPage);
        } catch (error: any) {
            notify('Errore stop feed.', 'error');
        }
    };

    const handleDeleteFeed = async (id: number) => {
        if (window.confirm('Sicuro di voler eliminare questo feed?')) {
            try {
                await api.delete(`/admin/feeds/${id}`);
                notify('Feed eliminato!', 'success');
                fetchFeeds(currentPage);
            } catch (error: any) {
                notify('Errore eliminazione: ' + error.message, 'error');
            }
        }
    };

    const handleDeleteScript = async (id: number) => {
        if (window.confirm('Sicuro di voler eliminare questo script?')) {
            try {
                await api.delete(`/admin/scripts/${id}`);
                notify('Script eliminato!', 'success');
                fetchScripts();
            } catch (error: any) {
                notify('Errore eliminazione script: ' + error.message, 'error');
            }
        }
    };

    const openEditScript = (script: Script) => {
        setScriptFormData({
            id: script.id,
            name: script.name,
            description: script.description || '',
            path: script.path,
            parameters: script.parameters
        });
        setIsScriptModalOpen(true);
    };

    const openEditFeed = (feed: Feed) => {
        // Find the script to get the script_id (might need script name matching if id isn't in feed)
        // Actually, our API should probably return script_id in Feed object.
        // Let's check admin.py get_feeds again.
        // Ah, it returns script_name but not script_id. We might need to find it by name or update API.
        const script = scripts.find(s => s.name === feed.script_name);
        setFeedFormData({
            id: feed.id,
            name: feed.name,
            script_id: script ? script.id.toString() : '',
            parameters: feed.parameters ? feed.parameters.map(p => ({ label: p.label, value: p.value })) : []
        });
        setIsFeedModalOpen(true);
    };

    const onScriptSelect = (scriptId: string) => {
        const script = scripts.find(s => s.id === parseInt(scriptId));
        if (script) {
            setFeedFormData({
                ...feedFormData,
                script_id: scriptId,
                parameters: script.parameters.map(p => ({ label: p.label, value: p.value || '' }))
            });
        } else {
            setFeedFormData({ ...feedFormData, script_id: '', parameters: [] });
        }
    };

    const addParameterToScript = () => {
        setScriptFormData({
            ...scriptFormData,
            parameters: [...scriptFormData.parameters, { label: '', type: 'stringa', required: false, value: '' }]
        });
    };

    const removeParameterFromScript = (index: number) => {
        const newParams = [...scriptFormData.parameters];
        newParams.splice(index, 1);
        setScriptFormData({ ...scriptFormData, parameters: newParams });
    };

    const updateParameterInScript = (index: number, field: keyof Parameter, value: any) => {
        const newParams = [...scriptFormData.parameters];
        newParams[index] = { ...newParams[index], [field]: value };
        setScriptFormData({ ...scriptFormData, parameters: newParams });
    };

    return (
        <div className="container">
            <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h2>FEED</h2>
                    <button className="btn btn-primary" onClick={() => { setFeedFormData({ id: null, name: '', script_id: '', parameters: [] }); setIsFeedModalOpen(true); }}>NUOVO FEED</button>
                </div>

                <table className="table-responsive">
                    <thead>
                        <tr>
                            <th>Nome</th>
                            <th>Script</th>
                            <th>Inizio / Fine</th>
                            <th>Durata</th>
                            <th>Status/Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {feeds.map(feed => (
                            <tr key={feed.id}>
                                <td>{feed.name}</td>
                                <td>{feed.script_name}</td>
                                <td style={{ fontSize: '0.7rem' }}>
                                    {feed.start_time && <div>Start: {new Date(feed.start_time).toLocaleString()}</div>}
                                    {feed.end_time && <div>End: {new Date(feed.end_time).toLocaleString()}</div>}
                                </td>
                                <td>{feed.duration || '-'}</td>
                                <td>
                                    <ActionMenu actions={[
                                        ...(feed.status === 'Running'
                                            ? [{ label: 'Stop', icon: 'fas fa-stop', onClick: () => stopFeed(feed.id), color: 'var(--error-color)' }]
                                            : [{ label: 'Start', icon: 'fas fa-play', onClick: () => startFeed(feed.id), color: 'green' }]
                                        ),
                                        { label: 'Log', icon: 'fas fa-clipboard-list', onClick: () => fetchFeedLog(feed.id) },
                                        { label: 'Modifica', icon: 'fas fa-pen-to-square', onClick: () => openEditFeed(feed) },
                                        { label: 'Resetta Stato', icon: 'fas fa-sync', onClick: () => resetFeed(feed.id), color: 'black' },
                                        { label: 'Rimuovi', icon: 'fas fa-trash-can', onClick: () => handleDeleteFeed(feed.id), color: 'var(--error-color)' },
                                    ]} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Pagination */}
                <div className="pagination" style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Precedente</button>
                    <span>Pagina {currentPage} di {totalPages} ({totalFeeds} feed)</span>
                    <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Successiva</button>
                </div>
            </div>

            <div className="card" style={{ marginTop: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h3>Script Disponibili</h3>
                    <button className="btn btn-secondary" onClick={() => { setScriptFormData({ id: null, name: '', description: '', path: '', parameters: [] }); setIsScriptModalOpen(true); }}>Nuovo Script</button>
                </div>
                <table className="table-responsive">
                    <thead>
                        <tr>
                            <th>Nome</th>
                            <th>Descrizione</th>
                            <th>Path</th>
                            <th>Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {scripts.map(s => (
                            <tr key={s.id}>
                                <td>{s.name}</td>
                                <td style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{s.description}</td>
                                <td style={{ fontSize: '0.8rem' }}>{s.path}</td>
                                <td>
                                    <ActionMenu actions={[
                                        { label: 'Modifica', icon: 'fas fa-pen-to-square', onClick: () => openEditScript(s) },
                                        { label: 'Elimina', icon: 'fas fa-trash-can', onClick: () => handleDeleteScript(s.id), color: 'var(--error-color)' },
                                    ]} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Feed Modal */}
            {isFeedModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '600px' }}>
                        <h3>{feedFormData.id ? 'Modifica Feed' : 'Crea Nuovo Feed'}</h3>
                        <form onSubmit={handleFeedSubmit}>
                            <div className="form-group">
                                <label>Nome Feed</label>
                                <input type="text" value={feedFormData.name} onChange={e => setFeedFormData({ ...feedFormData, name: e.target.value })} required />
                            </div>
                            <div className="form-group">
                                <label>Script</label>
                                <select
                                    value={feedFormData.script_id}
                                    onChange={e => onScriptSelect(e.target.value)}
                                    required
                                    disabled={!!feedFormData.id}
                                >
                                    <option value="">Seleziona Script...</option>
                                    {scripts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>

                            {feedFormData.parameters.length > 0 && (
                                <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                                    <h4>Parametri Script</h4>
                                    {feedFormData.parameters.map((p, i) => {
                                        const scriptParam = scripts.find(s => s.id === parseInt(feedFormData.script_id))?.parameters.find(sp => sp.label === p.label);
                                        return (
                                            <div key={p.label} className="form-group">
                                                <label>{p.label} {scriptParam?.required && <span style={{ color: 'red' }}>*</span>}</label>
                                                <input
                                                    type={scriptParam?.type === 'intero' ? 'number' : 'text'}
                                                    value={p.value}
                                                    onChange={e => {
                                                        const newParams = [...feedFormData.parameters];
                                                        newParams[i].value = e.target.value;
                                                        setFeedFormData({ ...feedFormData, parameters: newParams });
                                                    }}
                                                    required={scriptParam?.required}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                                <button type="submit" className="btn btn-primary">{feedFormData.id ? 'Salva' : 'Crea'}</button>
                                <button type="button" className="btn btn-text" onClick={() => setIsFeedModalOpen(false)}>Chiudi</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Script Modal */}
            {isScriptModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '700px' }}>
                        <h3>{scriptFormData.id ? 'Modifica Script' : 'Nuovo Script'}</h3>
                        <form onSubmit={handleScriptSubmit}>
                            <div className="form-group">
                                <label>Nome Script</label>
                                <input type="text" value={scriptFormData.name} onChange={e => setScriptFormData({ ...scriptFormData, name: e.target.value })} required />
                            </div>
                            <div className="form-group">
                                <label>Descrizione (max 3 righe)</label>
                                <textarea rows={3} value={scriptFormData.description} onChange={e => setScriptFormData({ ...scriptFormData, description: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Script Path (usa il prefisso app/ per path relativi alla root, es: app/script/...)</label>
                                <input type="text" value={scriptFormData.path} onChange={e => setScriptFormData({ ...scriptFormData, path: e.target.value })} required />
                            </div>

                            <div style={{ marginTop: '20px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h4>Parametri</h4>
                                    <button type="button" className="btn btn-secondary btn-sm" onClick={addParameterToScript}>+ Aggiungi</button>
                                </div>
                                {scriptFormData.parameters.map((p, i) => (
                                    <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'flex-end', background: '#f9f9f9', padding: '10px', borderRadius: '4px' }}>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ fontSize: '0.7rem' }}>Label</label>
                                            <input type="text" value={p.label} onChange={e => updateParameterInScript(i, 'label', e.target.value)} required />
                                        </div>
                                        <div style={{ width: '100px' }}>
                                            <label style={{ fontSize: '0.7rem' }}>Tipo</label>
                                            <select value={p.type} onChange={e => updateParameterInScript(i, 'type', e.target.value as any)}>
                                                <option value="stringa">Stringa</option>
                                                <option value="intero">Intero</option>
                                            </select>
                                        </div>
                                        <div style={{ width: '80px', textAlign: 'center' }}>
                                            <label style={{ fontSize: '0.7rem', display: 'block' }}>Obblig.</label>
                                            <input type="checkbox" checked={p.required} onChange={e => updateParameterInScript(i, 'required', e.target.checked)} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ fontSize: '0.7rem' }}>Valore Default (puoi usare app/...)</label>
                                            <input type="text" value={p.value} onChange={e => updateParameterInScript(i, 'value', e.target.value)} />
                                        </div>
                                        <button type="button" className="btn btn-text" style={{ color: 'red' }} onClick={() => removeParameterFromScript(i)}><i className="fas fa-xmark"></i></button>
                                    </div>
                                ))}
                            </div>

                            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                                <button type="submit" className="btn btn-primary">{scriptFormData.id ? 'Aggiorna' : 'Crea'}</button>
                                <button type="button" className="btn btn-text" onClick={() => setIsScriptModalOpen(false)}>Chiudi</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Log Modal */}
            {isLogModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '800px', width: '90%' }}>
                        <h3>Script Log</h3>
                        <pre style={{
                            background: '#1e1e1e',
                            color: '#d4d4d4',
                            padding: '15px',
                            borderRadius: '4px',
                            maxHeight: '500px',
                            overflowY: 'auto',
                            fontSize: '0.85rem',
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace'
                        }}>
                            {selectedLog || 'Nessun log disponibile.'}
                        </pre>
                        <div style={{ marginTop: '20px', textAlign: 'right' }}>
                            <button className="btn btn-primary" onClick={() => setIsLogModalOpen(false)}>Chiudi</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Removed local message display, replaced by global notify */}
        </div>
    );
};

export default ManageIngestion;
